// team-grid.js

export class TeamGrid {
  constructor(containerEl) {
    // The container that holds this particular team grid instance
    this.containerEl = containerEl;

    // Only look for team members within this container
    this.teamMembers = this.containerEl.querySelectorAll(".team-member");

    // If no members found, no further setup needed
    if (this.teamMembers.length === 0) return;

    // Initialize
    this.init();
  }

  init() {
    // Set up ARIA attributes, roles, etc. for each team member
    this.teamMembers.forEach((member) => {
      member.setAttribute("tabindex", "0");
      member.setAttribute("role", "button");

      if (member.dataset.view === "accordion") {
        member.setAttribute("aria-expanded", "false");
      } else if (member.dataset.view === "popup") {
        const bio = member.querySelector(".team-member__bio");
        if (bio) {
          bio.setAttribute("aria-hidden", "true");
        }
      }
    });

    // Attach event listeners within this container only
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Use container-level event delegation rather than document-level
    this.containerEl.addEventListener("click", this.handleClick.bind(this));
    this.containerEl.addEventListener("keydown", this.handleKeyDown.bind(this));
  }

  handleClick(event) {
    // Identify which .team-member was clicked
    const teamMember = event.target.closest(".team-member");
    if (!teamMember || !this.containerEl.contains(teamMember)) return;

    // Check if the close button was clicked
    const closeButton = event.target.closest(".team-member__close");
    const bio = teamMember.querySelector(".team-member__bio");
    const bioInner = bio?.querySelector(".wp-block-group");

    // If close button was clicked
    if (closeButton) {
      this.closeMember(teamMember);
      return;
    }

    // If click happened inside the bio but not on wp-block-group content
    if (bio?.contains(event.target) && !bioInner?.contains(event.target)) {
      this.closeMember(teamMember);
      return;
    }

    // If click is outside the bio area, toggle the member
    if (!bio?.contains(event.target)) {
      this.toggleMember(teamMember);
    }
  }

  handleKeyDown(event) {
    const teamMember = event.target.closest(".team-member");
    if (!teamMember || !this.containerEl.contains(teamMember)) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggleMember(teamMember);
    }

    if (event.key === "Escape") {
      this.closeMember(teamMember);
    }
  }

  toggleMember(teamMember) {
    const view = teamMember.dataset.view;

    if (view === "accordion") {
      const isExpanded = teamMember.getAttribute("aria-expanded") === "true";

      // Close any open accordion siblings
      if (!isExpanded) {
        this.teamMembers.forEach((otherMember) => {
          if (
            otherMember !== teamMember &&
            otherMember.dataset.view === "accordion" &&
            otherMember.getAttribute("aria-expanded") === "true"
          ) {
            otherMember.setAttribute("aria-expanded", "false");
          }
        });
      }

      // Toggle current
      teamMember.setAttribute("aria-expanded", String(!isExpanded));
    } else if (view === "popup") {
      const bio = teamMember.querySelector(".team-member__bio");
      if (!bio) return;

      const isHidden = bio.getAttribute("aria-hidden") === "true";
      bio.setAttribute("aria-hidden", String(!isHidden));
      document.querySelector("html").classList.add("body-overlay", !isHidden);
    }
  }

  closeMember(teamMember) {
    const bio = teamMember.querySelector(".team-member__bio");
    if (!bio) return;

    bio.setAttribute("aria-hidden", "true");
    document.querySelector("html").classList.remove("body-overlay");
  }
}
